import { Component, effect, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { AgentBuilderService } from '../services/agent-builder.service';
import { urlOrDefaultValidator } from '../../../../../factory/custom-validators';
import { CheckableSettings, TreeItemLookup, TreeViewModule } from '@progress/kendo-angular-treeview';
import { SENSITIVITY_LABELS_FLAT } from '../../../../../constants/sensitivity.constants';
import { ISensitivityLabelExpanded } from '../../../../../interfaces/i-sensitivity-label';
import { MatIconModule } from '@angular/material/icon';
import { ProfileService } from '../../../../../services/profile.service';
import { AgentService } from '../../../services/agent.service';
import { KeywordsControllerComponent } from '../../../../../shared/keywords-controller/keywords-controller.component';
import { KeywordService } from '../../../../../services/keyword.service';
import { computed } from '@angular/core';

@Component({
  selector: 'agent-details',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TreeViewModule,
    KeywordsControllerComponent
  ],
  templateUrl: './agent-details.component.html',
  styleUrl: './agent-details.component.scss'
})
export class AgentDetailsComponent {
  agentForm!: FormGroup;

  // Kendo UI TreeView
  expandedKeys: any[] = ['0'];
  checkedKeys: any[] = [];
  sensitivityLabelData: ISensitivityLabelExpanded[] = SENSITIVITY_LABELS_FLAT;
  checkableSettings: CheckableSettings = {
    checkChildren: true,
    checkDisabledChildren: true, // Check all children
    checkParents: true,
    enabled: true,  // Enable checkable mode
    mode: 'multiple', // Multiple selection mode
    checkOnClick: true, // Check all parents when children are checked
    uncheckCollapsedChildren: false // Uncheck all children when parent is unchecked,
  };

  // Hardcoded keywords (kept for reference/fallback)
  hardcodedKeywordStrings = [ 'Proposal', 'Contract', 'Invoice', 'Report', 'Meeting Notes', 'Presentation', 'Budget', 'Plan', 'Summary', 'Analysis' ];
  // T - 2556 + 5430
  // This is used to determine whether or not to disable the checkboxes for Sensitivity Labels
  // based on whether a user has Manage or Create Agent permissions and if we are editing an Agent.
  isSensitivityLabelDisabled = (): boolean => {
      return (this.#profileService.userProfile()?.config?.canManageAgents == false &&
              this.#profileService.userProfile()?.config?.canCreateAgents == false &&
              this.#agentService.isEditAgent() == true);
  }
  // END Kendo UI TreeView

  #formBuilder = inject(FormBuilder);
  #agentBuilderService = inject(AgentBuilderService);
  #profileService = inject(ProfileService);
  #agentService = inject(AgentService);
  #keywordService = inject(KeywordService);

  // Merge API keywords with legacy hardcoded strings
  allKeywords = computed(() =>
    this.#keywordService.mergeWithLegacyStrings(this.hardcodedKeywordStrings)
  );
  isLoadingKeywords = this.#keywordService.isLoading;

  constructor() {
    // Load keywords from API
    this.#keywordService.loadKeywords();
    effect(() => {
      if (this.#agentBuilderService.agent()) {
        // If the agent has sensitivity labels, set the checkedKeys to the agent's sensitivity labels
        // so that the checkboxes are selected in the treeview.
        if (this.#agentBuilderService.agent().details.sensitivityLabels && this.#agentBuilderService.agent().details.sensitivityLabels.length > 0) {
          this.checkedKeys = this.#agentBuilderService.agent().details.sensitivityLabels.map(item => item.id);
        }

        this.agentForm = this.#formBuilder.group({
          name: new FormControl(this.#agentBuilderService.agent().details.name, [Validators.required]),
          description: new FormControl(this.#agentBuilderService.agent().details.description, [Validators.required]),
          keywords: new FormControl(this.#agentBuilderService.agent().details.keywords),
          supportUrl: new FormControl(this.#agentBuilderService.agent().details.supportUrl, [urlOrDefaultValidator]),
          sensitivityLabels: new FormControl(this.checkedKeys, [this.sensitivityLabelValidator.bind(this)]),
        });
      }
    });
  }

  /**
   * Decides whether the checkbox should be displayed for the item.
   */
  hasCheckbox = (item: any, index: string): boolean => {
    return !(item.parentId === 15 || item.id === 15);
  };

  /**
   * Fired when the list of checked keys changes (including label clicks when checkOnClick is enabled).
   * Keeps the form control in sync with the TreeView checked state.
   */
  onCheckedKeysChange(checkedKeys: any[]): void {
    this.checkedKeys = checkedKeys;
    this.agentForm.get('sensitivityLabels')?.setValue([...checkedKeys]);
    this.agentForm.get('sensitivityLabels')?.markAsTouched();
    this.agentForm.get('sensitivityLabels')?.updateValueAndValidity();
  }

  sensitivityLabelValidator(control: AbstractControl): ValidationErrors | null {
    if (control.get('sensitivityLabels')?.value.length === 0 && control.get('sensitivityLabels')?.touched) {
      return { sensitivityLabelInvalid: true };
    }
    return null;
  }

  /**
   * Marks all controls in the agent form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the AgentBuilderComponent.
   */
  markFormAsTouched(): void {
    this.agentForm.markAllAsTouched();
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.agentForm) {
      return false;
    }

    // Mark all fields as touched first to show validation errors
    this.agentForm.markAllAsTouched();

    // Reset form errors
    this.agentForm.get('name')?.setErrors(null);
    this.agentForm.get('description')?.setErrors(null);
    this.agentForm.get('sensitivityLabels')?.setErrors(null);

    // Validate required fields
    if (this.agentForm.get('name')?.value === '') {
      this.agentForm.get('name')?.setErrors({ required: true });
      this.agentForm.get('name')?.markAsTouched();
    }
    if (this.agentForm.get('description')?.value === '') {
      this.agentForm.get('description')?.setErrors({ required: true });
      this.agentForm.get('description')?.markAsTouched();
    }

    // Check if the sensitivityLabels is empty.
    // This is a special case because the sensitivityLabels is a treeview and cannot be validated using the normal form validation.
    if (this.agentForm.get('sensitivityLabels')?.value === null || this.agentForm.get('sensitivityLabels')?.value.length === 0) {
      this.agentForm.get('sensitivityLabels')?.setErrors({ sensitivityLabelInvalid: true });
      this.agentForm.get('sensitivityLabels')?.markAsTouched();
    }

    return this.agentForm.valid;
  }

    /**
   * Method to update the checkbox state from the parent component
   */
    updateCheckedKeys(checkedKeys: any[]): void {
      this.checkedKeys = checkedKeys;
      this.agentForm.get('sensitivityLabels')?.setValue(checkedKeys);
    }

  /**
   * Updates the agent builder service with the current values from the agent form.
   * Maps selected sensitivity label IDs to their full label objects before updating.
   */
  updateAgent(): void {
    // Map checkedKeys (ids) to full label objects
    const selectedLabels = this.checkedKeys
      .map(id => this.sensitivityLabelData.find(label => label.id === id))
      .filter(label => !!label);

    this.#agentBuilderService.agent.update(prev => ({
      ...prev,
      details: {
        ...prev?.details,
        ...this.agentForm.value,
        sensitivityLabels: selectedLabels
      }
    }));
  }

  /**
   * Handler for the keywordsChanged event emitted by the KeywordsControllerComponent.
   * Called whenever the keywords control value changes in the child component.
   * Updates the agent state with the latest form values.
   *
   * This is needed when the user invalidates the control and clicks the save button.
   */
  onKeywordsChanged() {
    this.updateAgent();
  }
}
